import { makeAutoObservable } from "mobx"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { GapDialogChildOptions, GapDialogStore } from "reusable/gap-dialog/GapDialogStore"
import { GapDialogAction } from "reusable/gap-dialog/GapDialogActionButton"
import { formHandler } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { validateEmail } from "reusable/utils/validation"
import { snackbarError, snackbarSuccess } from "reusable/mui/snackbar/snackbar"
import { GapAutocompleteData } from "features/gap-components/autocomplete/types/GapAutocompleteTypes"
import { apiClient } from "api/trpc/trpc-client"

type ImportFieldId = 'clientName' | 'primaryContactFirstName' | 'primaryContactLastName' | 'primaryContactEmail'

const requiredFields: ImportFieldId[] = ['clientName', 'primaryContactFirstName', 'primaryContactLastName', 'primaryContactEmail']

const importFieldLabels: Record<ImportFieldId, string> = {
   clientName: 'Client name',
   primaryContactFirstName: 'First name',
   primaryContactLastName: 'Last name',
   primaryContactEmail: 'Email address',
}

const missingValueMessages: Record<ImportFieldId, string> = {
   clientName: 'Missing client’s organisation name',
   primaryContactFirstName: 'Missing primary contact first name',
   primaryContactLastName: 'Missing primary contact last name',
   primaryContactEmail: 'Missing primary contact email address',
}

interface RowIssue {
   lineNumber: number
   column: string
   message: string
}

interface CreateClientCSVImportFieldsStoreProps {
   gapDialogStore: GapDialogStore
}

export default class CreateClientCSVImportFieldsStore {
   sp

   fields: Record<string, string> = {}
   errorMessage: string | null = null
   clientNames: string[] = []
   clientNamesRequest: Promise<void> | undefined = undefined
   rows: Record<string, any>[] = []
   rowLineNumbers: number[] = []
   step: 'mapping' | 'review' = 'mapping'
   /** Issues found when the review step opened, so their fields stay visible while being fixed. */
   reviewIssues: RowIssue[] = []

   constructor(sp: CreateClientCSVImportFieldsStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
      this.setPrimaryButton({ callback: this.onImportButtonClick, name: 'Import', disabled: true, notCloseAutomatically: true })

      if (!this.dataError) {
         this.rows = this.data.rows.map(row => ({ ...row }))
         this.rowLineNumbers = [...this.data.lineNumbers]
         this.fields = this.data.fieldNames.reduce((acc, fieldName) => {
            let value = 'skip'
            const field = fieldName.toLowerCase()
            // Try to preselect the field based on the column name. Client should go first, so we can override later if we find a better match(ex. for field "client first name")
            if (field.includes('client') || field.includes('company')) {
               value = 'clientName'
            }

            if (field.includes('first')) {
               value = 'primaryContactFirstName'
            }

            if (field.includes('last')) {
               value = 'primaryContactLastName'
            }

            if (field.includes('email')) {
               value = 'primaryContactEmail'
            }

            acc[fieldName] = value
            return acc
         }, {} as Record<string, string>)

         this.checkFieldsForCorrectness()
      }
   }

   get dataError() {
      if (!this.sp.gapDialogStore.childOptions?.data || !this.sp.gapDialogStore.childOptions.data.rows || !this.sp.gapDialogStore.childOptions.data.fieldNames) {
         return "We couldnt find any data to import."
      }


      if (!(this.sp.gapDialogStore.childOptions.data.fieldNames.length >= 4)) {
         return "CSV file doesnt contain enough columns. Expected at least 4 columns."
      }

      return null
   }

   get data() {
      return this.sp.gapDialogStore.childOptions!.data as {
         fieldNames: string[]
         lineNumbers: number[]
         onClientsImported?: () => void
         rows: {
            [key: string]: any
         }[]
      }
   }

   get dropdownOptions(): GapAutocompleteData {
      return {
         items: [{ id: 'skip', label: 'Skip this column' }],
         categories: [
            {
               id: 'client',
               label: 'Client',
               items: [{ id: 'clientName', label: importFieldLabels.clientName }],
            },
            {
               id: 'primaryContact',
               label: 'Primary contact',
               items: [
                  { id: 'primaryContactFirstName', label: importFieldLabels.primaryContactFirstName },
                  { id: 'primaryContactLastName', label: importFieldLabels.primaryContactLastName },
                  { id: 'primaryContactEmail', label: importFieldLabels.primaryContactEmail },
               ],
            },
         ],
      }
   }

   get formHandler() {
      const fieldConfigs = this.data.fieldNames.reduce((configs, fieldName) => {
         configs[fieldName] = { type: 'string' }
         return configs
      }, {} as Record<string, { type: 'string' }>)

      return formHandler(this.fields, fieldConfigs, {
         onValueChange: this.checkFieldsForCorrectness
      })
   }

   /** Maps each import field to the CSV column it was mapped to. */
   get columnByImportField() {
      return Object.keys(this.fields).reduce((columns, column) => {
         const importField = this.fields[column]
         if (importField !== 'skip') {
            columns[importField as ImportFieldId] = column
         }
         return columns
      }, {} as Partial<Record<ImportFieldId, string>>)
   }

   get rowIssues(): RowIssue[] {
      if (this.errorMessage) {
         return []
      }

      const clientNameColumn = this.columnByImportField.clientName
      const clientNameCounts = this.rows.reduce((counts, row) => {
         const clientName = clientNameColumn ? String(row[clientNameColumn] ?? '').trim() : ''
         counts.set(clientName, (counts.get(clientName) ?? 0) + 1)
         return counts
      }, new Map<string, number>())

      return this.rows.flatMap((row, index) => {
         const lineNumber = this.rowLineNumbers[index]
         return Object.keys(this.fields).flatMap(column => {
            const importField = this.fields[column] as ImportFieldId | 'skip'
            if (importField === 'skip') {
               return []
            }
            const value = String(row[column] ?? '').trim()
            const message = this.issueMessage({ importField, value, clientNameCount: clientNameCounts.get(value) ?? 0 })
            return message ? [{ lineNumber, column, message }] : []
         })
      })
   }

   /** Groups the review issues by CSV line, keeping fixed issues and adding any new ones. */
   get reviewGroups() {
      const issueKey = (issue: RowIssue) => `${issue.lineNumber}-${issue.column}`
      const liveIssueByKey = new Map(this.rowIssues.map(issue => [issueKey(issue), issue]))
      const shownKeys = new Set(this.reviewIssues.map(issueKey))
      const shownIssues = [...this.reviewIssues, ...this.rowIssues.filter(issue => !shownKeys.has(issueKey(issue)))]

      const lineNumbers = [...new Set(shownIssues.map(issue => issue.lineNumber))]
         .filter(lineNumber => this.rowLineNumbers.includes(lineNumber))
         .sort((left, right) => left - right)

      return lineNumbers.map(lineNumber => {
         const row = this.rows[this.rowLineNumbers.indexOf(lineNumber)]
         const clientNameColumn = this.columnByImportField.clientName
         const emailColumn = this.columnByImportField.primaryContactEmail
         const clientName = clientNameColumn ? String(row[clientNameColumn] ?? '').trim() : ''
         const email = emailColumn ? String(row[emailColumn] ?? '').trim() : ''
         return {
            lineNumber,
            description: [clientName || 'unknown client name', email].filter(Boolean).join(', '),
            cells: shownIssues
               .filter(issue => issue.lineNumber === lineNumber)
               .map(issue => {
                  const liveIssue = liveIssueByKey.get(issueKey(issue))
                  return {
                     column: issue.column,
                     label: importFieldLabels[this.fields[issue.column] as ImportFieldId],
                     value: String(row[issue.column] ?? ''),
                     message: liveIssue?.message ?? issue.message,
                     isResolved: !liveIssue,
                  }
               }),
         }
      })
   }

   /** Returns why a mapped cell can't be imported, or undefined when it can. */
   issueMessage(params: { importField: ImportFieldId, value: string, clientNameCount: number }) {
      const { importField, value, clientNameCount } = params
      if (!value) {
         return missingValueMessages[importField]
      }
      if (importField === 'primaryContactEmail' && !validateEmail(value)) {
         return 'Invalid primary contact email address'
      }
      if (importField === 'clientName' && this.clientNames.includes(value)) {
         return 'A client with this name already exists'
      }
      if (importField === 'clientName' && clientNameCount > 1) {
         return 'This client name appears more than once in your CSV'
      }
      return undefined
   }

   checkFieldsForCorrectness = () => {
      let duplicateFound = false
      let missingAttribute = false

      Object.values(this.fields).forEach(value => {
         if (Object.values(this.fields).filter(v => v === value && value !== 'skip').length > 1) {
            duplicateFound = true
         }
      })

      const selectedFields = Object.values(this.fields)
      if (requiredFields.some(requiredField => !selectedFields.includes(requiredField))) {
         missingAttribute = true
      }

      if (duplicateFound && missingAttribute) {
         this.errorMessage = 'You cannot have duplicate fields and missing attributes.'
      } else if (missingAttribute) {
         this.errorMessage = 'You must select a field for each column.'
      } else if (duplicateFound) {
         this.errorMessage = 'You cannot have duplicate fields.'
      } else {
         this.errorMessage = null
      }

      const hasBlockingIssues = this.step === 'review' && this.rowIssues.length > 0
      this.setPrimaryButton({ disabled: duplicateFound || missingAttribute || this.rows.length === 0 || hasBlockingIssues })
   }

   /** Replaces the dialog's child options so the title and buttons re-render with the given changes. */
   setChildOptions = (changes: Partial<GapDialogChildOptions>) => {
      const childOptions = this.sp.gapDialogStore.childOptions
      if (!childOptions) {
         return
      }
      this.sp.gapDialogStore.childOptions = { ...childOptions, ...changes }
   }

   setPrimaryButton = (changes: Partial<GapDialogAction>) => {
      const primaryButton = this.sp.gapDialogStore.childOptions?.primaryButton
      this.setChildOptions({ primaryButton: { name: 'Import', ...primaryButton, ...changes } })
   }

   onImportButtonClick = async () => {
      this.setPrimaryButton({ loading: true })
      try {
         await this.fetchClientNames()
      } catch (error) {
         snackbarError('We had trouble checking your existing clients. Try importing again.', { error })
         return
      } finally {
         this.setPrimaryButton({ loading: false })
      }

      if (this.step === 'mapping' && this.rowIssues.length > 0) {
         this.openReviewStep()
         return
      }
      await this.startImport()
   }

   openReviewStep = () => {
      this.reviewIssues = this.rowIssues
      this.step = 'review'
      const rowCount = new Set(this.reviewIssues.map(issue => issue.lineNumber)).size
      this.setChildOptions({
         title: `${rowCount} ${rowCount === 1 ? 'row needs' : 'rows need'} fixing`,
         subTitle: 'Fix these rows or remove them from the import, then import your clients.',
      })
      this.checkFieldsForCorrectness()
   }

   reviewFieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      const lineNumber = Number(event.target.getAttribute('data-line-number'))
      const column = event.target.getAttribute('data-column')
      const row = this.rows[this.rowLineNumbers.indexOf(lineNumber)]
      if (!row || !column) {
         return
      }
      row[column] = event.target.value
      this.checkFieldsForCorrectness()
   }

   removeReviewRowButtonHandler = (event: React.MouseEvent<HTMLElement>) => {
      const rowIndex = this.rowLineNumbers.indexOf(Number(event.currentTarget.getAttribute('data-line-number')))
      if (rowIndex === -1) {
         return
      }
      this.rows.splice(rowIndex, 1)
      this.rowLineNumbers.splice(rowIndex, 1)
      this.checkFieldsForCorrectness()
   }

   /** Fetches the existing client names once, reusing the request that is already in flight. */
   fetchClientNames = () => {
      this.clientNamesRequest ??= this.loadClientNames()
      return this.clientNamesRequest
   }

   /** Starts the client name fetch early; a failure resurfaces when Import awaits it again. */
   prefetchClientNames = () => {
      this.fetchClientNames().catch(() => undefined)
   }

   loadClientNames = async () => {
      try {
         this.setClientNames(await apiClient.advisor.getMyClientNames.query())
      } catch (error) {
         this.clientNamesRequest = undefined
         throw error
      }
   }

   setClientNames = (clientNames: string[]) => {
      this.clientNames = clientNames
   }

   startImport = async () => {
      const columns = this.columnByImportField
      const clients = this.rows.map(row => ({
         name: String(row[columns.clientName!]).trim(),
         primaryContactFirstName: String(row[columns.primaryContactFirstName!]).trim(),
         primaryContactLastName: String(row[columns.primaryContactLastName!]).trim(),
         email: String(row[columns.primaryContactEmail!]).trim(),
      }))

      this.setPrimaryButton({ loading: true })
      try {
         await apiClient.advisor.bulkAddClients.mutate({ clients })
         snackbarSuccess(`${clients.length} ${clients.length === 1 ? 'client' : 'clients'} imported`)
         this.data.onClientsImported?.()
         await this.sp.gapDialogStore.handleClose()
      } catch (error) {
         snackbarError('We had trouble importing your clients. Nothing was imported, so you can safely try again.', { error })
      } finally {
         this.setPrimaryButton({ loading: false })
      }
   }
}

export const { Provider: CreateClientCSVImportFieldsStoreProvider, useProvidedStore: useCreateClientCSVImportFieldsStore } = createStoreContext(CreateClientCSVImportFieldsStore)
